import { HttpErrorResponse } from '@angular/common/http';
import { translate as t } from './i18n';

export type ConflictKind = 'none' | 'version' | 'business';
export type ConflictSource = 'none' | 'error-code' | 'details-reason' | 'legacy-status';
export type BusinessConflictReason = 'single-admin' | 'admin-race' | 'generic';

export interface ConflictClassification {
  kind: ConflictKind;
  source: ConflictSource;
  errorCode: string | null;
  reason: string | null;
  businessReason: BusinessConflictReason | null;
}

const VERSION_CONFLICT_MARKERS = ['VERSION', 'OPTIMISTIC', 'STALE', 'ETAG'] as const;
const SINGLE_ADMIN_MARKERS = ['SINGLE_ADMIN', 'LAST_ADMIN', 'ADMIN_REQUIRED', 'SOLE_ADMIN'] as const;
const ADMIN_RACE_MARKERS = ['ADMIN_RACE', 'ADMIN_ROLE_RACE', 'ADMIN_CONCURRENCY'] as const;

export function classifyConflict(error: unknown): ConflictClassification {
  if (!(error instanceof HttpErrorResponse) || error.status !== 409) {
    return {
      kind: 'none',
      source: 'none',
      errorCode: null,
      reason: null,
      businessReason: null,
    };
  }

  const body = error.error;
  const errorCode = normalizeStructuredValue(
    body && typeof body === 'object' ? body['errorCode'] : null
  );
  const reason = normalizeStructuredValue(extractDetailsReason(body));

  if (matchesMarker(errorCode, VERSION_CONFLICT_MARKERS)) {
    return {
      kind: 'version',
      source: 'error-code',
      errorCode,
      reason,
      businessReason: null,
    };
  }

  if (matchesMarker(reason, VERSION_CONFLICT_MARKERS)) {
    return {
      kind: 'version',
      source: 'details-reason',
      errorCode,
      reason,
      businessReason: null,
    };
  }

  if (errorCode || reason) {
    return {
      kind: 'business',
      source: errorCode ? 'error-code' : 'details-reason',
      errorCode,
      reason,
      businessReason: classifyBusinessReason(errorCode, reason),
    };
  }

  return {
    kind: 'version',
    source: 'legacy-status',
    errorCode,
    reason,
    businessReason: null,
  };
}

export function isVersionConflict(error: unknown): boolean {
  return classifyConflict(error).kind === 'version';
}

export function buildVersionConflictMessage(entityName: string): string {
  return t(
    'ui.core.concurrency.conflict.text.otro_usuario_actualizo_este_recurso',
    { entityName },
    `Se actualizó este ${entityName}. Revisa y guarda otra vez.`
  );
}

export function buildBusinessConflictMessage(
  classification: ConflictClassification
): string {
  if (classification.businessReason === 'single-admin') {
    return t(
      'ui.core.concurrency.conflict.text.no_se_puede_completar_la_operacion_porque_debe_quedar_al',
      undefined,
      'Debe quedar un admin activo.'
    );
  }
  if (classification.businessReason === 'admin-race') {
    return t(
      'ui.core.concurrency.conflict.text.otro_admin_cambio_los_permisos_al_mismo_tiempo_actualiza_l',
      undefined,
      'Otro admin cambió esto. Actualiza y prueba otra vez.'
    );
  }
  return t(
    'ui.core.concurrency.conflict.text.la_operacion_entro_en_conflicto_con_una_regla_de_negocio_act',
    undefined,
    'Hubo un cambio. Actualiza y prueba otra vez.'
  );
}

function classifyBusinessReason(
  errorCode: string | null,
  reason: string | null
): BusinessConflictReason {
  if (matchesMarker(errorCode, SINGLE_ADMIN_MARKERS) || matchesMarker(reason, SINGLE_ADMIN_MARKERS)) {
    return 'single-admin';
  }
  if (matchesMarker(errorCode, ADMIN_RACE_MARKERS) || matchesMarker(reason, ADMIN_RACE_MARKERS)) {
    return 'admin-race';
  }
  return 'generic';
}

function extractDetailsReason(body: unknown): unknown {
  if (!body || typeof body !== 'object') {
    return null;
  }
  const details = (body as Record<string, unknown>)['details'];
  if (!details || typeof details !== 'object') {
    return null;
  }
  return (details as Record<string, unknown>)['reason'];
}

function normalizeStructuredValue(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  return value
    .trim()
    .replace(/[\s\-]+/g, '_')
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toUpperCase();
}

function matchesMarker(descriptor: string | null, markers: readonly string[]): boolean {
  if (!descriptor) {
    return false;
  }
  return markers.some((marker) => descriptor.includes(marker));
}
